import React, { useState } from "react";
import { Card, Badge, Button, Row, Col } from "react-bootstrap";
import {
  FaMapMarkerAlt,
  FaDollarSign,
  FaTruck,
  FaClock,
  FaEye,
  FaBox,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  acceptOrder,
  fetchMyOrders,
  markAsComplete,
} from "../../features/buyer/buyerSlice";
import { showAppMessageToast } from "../../utils/toastUtils";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import theme from "../../styles/theme/theme";
import { getStatusBadgeClass } from "../../utils/stringUtils";
import RaiseDisputeModal from "./RaiseDisputeModal";
import { formatCurrency, formatPrice } from "../../utils/price";

const OrderCard = ({ order, onComplete, onRaiseDispute }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  console.log("OrderCard order:", order);

  // const getStatusVariant = (status) => {
  //   switch (status) {
  //     case "delivered":
  //       return "success";
  //     case "shipped":
  //       return "info";
  //     case "accepted":
  //       return "warning";
  //     case "pending":
  //       return "secondary";
  //     default:
  //       return "secondary";
  //   }
  // }; 

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} days ago`;
    }
  };

  const handleMarkAsComplete = () => {
    setLoading(true);
    dispatch(markAsComplete(order.id))
      .unwrap()
      .then(() => {
        showAppMessageToast({
          type: "success",
          title: "Marked as Complete",
          message: `Order #${order.id} has been marked as complete.`,
          navigate,
        });
        dispatch(fetchMyOrders());
      })
      .catch((err) => {
        showAppMessageToast({
          type: "error",
          title: "Failed to Mark Complete",
          message: err || "Something went wrong.",
          navigate,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleAcceptOrder = () => {
    // Implement accept order functionality here
    setLoading(true);
    dispatch(acceptOrder(order.id))
      .unwrap()
      .then(() => {
        showAppMessageToast({
          type: "success",
          title: "Order Accepted",
          message: `Order #${order.id} has been accepted.`,
          navigate,
        });
        dispatch(fetchMyOrders());
      })
      .catch((err) => {
        showAppMessageToast({
          type: "error",
          title: "Failed to Accept Order",
          message: err || "Something went wrong.",
          navigate,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const ORDER_STATUS_LABELS = {
    pending: "Pending",
    accepted: "Accepted",
    matched: "Matched",
    shipped: "Shipped",
    delivered: "Delivered",
    completed: "Completed",
    rejected: "Rejected",

    admin_review: "Admin Review",
    awaiting_extra_payment: "Awaiting Extra Payment",
  };

  const getOrderStatusLabel = (status) => {
    if (!status) return "-";

    return (
      ORDER_STATUS_LABELS[status] ||
      status
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    );
  };

  // console.log("Status:", order.status);

  return (
    <Card style={cardStyle} className="h-100">
      <Card.Header className="text-center border-0 bg-transparent">
        <div style={productImageStyle}>
          <FaBox size={32} color={theme.palette.primary.main} />
        </div>

        <Card.Title
          className="h6 mb-2"
          style={{ color: theme.palette.text.primary }}
        >
          {order.product?.name}
        </Card.Title>
        <Card.Subtitle
          className="mb-3 small"
          style={{ color: theme.palette.text.secondary }}
        >
          {order.seller?.name}
        </Card.Subtitle>

        <Badge
          bg={getStatusBadgeClass(order.status)}
          className="d-flex align-items-center justify-content-center gap-1 mx-auto"
          style={{ width: "fit-content", padding: "6px 12px" }}
        >
          <FaBox size={12} />
          {getOrderStatusLabel(order.status)}
        </Badge>
      </Card.Header>

      <Card.Body
        className="pt-0"
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        <div className="mb-4">
          <div style={detailRowStyle}>
            <div
              className="d-flex align-items-center"
              style={{ color: theme.palette.text.secondary }}
            >
              <FaMapMarkerAlt style={iconStyle} size={14} />
              <span>Location</span>
            </div>
            <span style={{ color: theme.palette.text.primary }}>
              {order.product?.location || "N/A"}
            </span>
          </div>

          <div style={detailRowStyle}>
            <div
              className="d-flex align-items-center"
              style={{ color: theme.palette.text.secondary }}
            >
              <FaDollarSign style={iconStyle} size={14} />
              <span>Total Price</span>
            </div>
            <span style={{ color: theme.palette.text.primary }}>
              {/* A$ {formatPrice(order.total_price)} */}
              {formatCurrency(order.total_price,order.currency)}
            </span>
          </div>

          <div style={detailRowStyle}>
            <div
              className="d-flex align-items-center"
              style={{ color: theme.palette.text.secondary }}
            >
              <FaTruck style={iconStyle} size={14} />
              <span>Tracking</span>
            </div>
            <span style={{ color: theme.palette.text.primary }}>
              {order.tracking_number || "N/A"}
            </span>
          </div>

          <div style={detailRowStyle}>
            <div
              className="d-flex align-items-center"
              style={{ color: theme.palette.text.secondary }}
            >
              <FaClock style={iconStyle} size={14} />
              <span>Ordered</span>
            </div>
            <span style={{ color: theme.palette.text.primary }}>
              {timeAgo(order.created_at)}
            </span>
          </div>
        </div>

        <div className="border-top pt-3 justify-content-center">
          <Row
            className={`g-2 justify-content-center ${
              order.status !== "accepted" ? "justify-content-center" : ""
            }`}
          >
            <Col xs="auto" sm={order.status !== "accepted" ? 5 : 4}>
              <Button
                variant="outline-primary"
                size="sm"
                style={buttonStyle}
                className="w-100 d-flex align-items-center justify-content-center"
                onClick={() => navigate(`/order-details/${order.id}`)}
              >
                <FaEye size={12} className="me-1" />
                <span>View</span>
              </Button>
            </Col>

            <Col xs="auto" sm={order.status !== "accepted" ? 5 : 4}>
              <Button
                variant="outline-secondary"
                size="sm"
                style={buttonStyle}
                className="w-100 d-flex align-items-center justify-content-center"
                onClick={() => navigate(`/order-track/${order.id}`)}
              >
                <FaTruck size={12} className="me-1" />
                <span>Track</span>
              </Button>
            </Col>

            {(order.status === "accepted" ||
              order.status === "delivered" ||
              order.payment_status === "pending") && (
              <Col xs="auto" sm={4}>
                {order.status === "accepted" && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    style={buttonStyle}
                    className="w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate(`/shipping-mode/${order.id}`)}
                  >
                    <FaDollarSign size={12} className="me-1" />
                    <span>Pay Now</span>
                  </Button>
                )}

                {/* {order.status === "delivered" && (                 
                  // <Button
                  //   variant="outline-success"
                  //   size="sm"
                  //   style={{
                  //     ...buttonStyle,
                  //     opacity: loading ? 0.6 : 1,
                  //     cursor: loading ? "not-allowed" : "pointer",
                  //   }}
                  //   className="w-100 d-flex align-items-center justify-content-center"
                  //   disabled={loading}
                  //   onClick={handleMarkAsComplete}
                  // >
                  //   {loading ? (
                  //     "Processing..."
                  //   ) : (
                  //     <>
                  //       <FaBox size={12} className="me-1" />
                  //       <span>Complete</span>
                  //     </>
                  //   )}
                  // </Button>
                )} */}
              </Col>
            )}
          </Row>
          {/* ROW 2: Accept + Dispute (ONLY for delivered) */}
          {order.status === "delivered" && (
            <Row className="g-2 justify-content-center mt-2">
              <Col xs={5}>
                <Button
                  variant="outline-success"
                  size="sm"
                  style={{
                    ...buttonStyle,
                    opacity: loading ? 0.6 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                  disabled={loading}
                  className="w-100 d-flex align-items-center justify-content-center"
                  onClick={handleAcceptOrder}
                >
                  <FaBox size={12} className="me-1" />
                  Accept
                </Button>
              </Col>

              <Col xs={5}>
                <Button
                  variant="outline-danger"
                  size="sm"
                  style={buttonStyle}
                  className="w-100 d-flex align-items-center justify-content-center"
                  onClick={() => onRaiseDispute()}
                >
                  <FaExclamationTriangle size={12} className="me-1" />
                  Raise Dispute
                </Button>
              </Col>
            </Row>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default OrderCard;

const cardStyle = {
  maxWidth: "400px",
  margin: "0 auto",
  boxShadow: theme.shadows[2],
  border: "none",
  borderRadius: "16px",
  backgroundColor: theme.palette.background.paper,
  transition: "all 0.3s ease",
};

const productImageStyle = {
  width: "80px",
  height: "80px",
  background: theme.palette.primary.light,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto",
};

const detailRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "8px",
  fontSize: "14px",
};

const iconStyle = {
  marginRight: "8px",
  color: theme.palette.text.secondary,
};

const buttonStyle = {
  fontSize: "12px",
  padding: "6px 12px",
  margin: "2px",
  flex: "1",
  whiteSpace: "nowrap",
};
